import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { updateUserProfileClient } from '@services/userProfileClient';

export interface ProfileData {
  name: string;
  phone: string;
  completionPercentage: number;
  avatar: string;
  email: string;
  ekey?: string;
  contact_no?: string;
  buying_period?: string;
  bookings?: number;
  is_onboarded: boolean;
}

interface ProfileState {
  data: ProfileData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  data: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunk for updating profile via API
export const updateProfileAsync = createAsyncThunk(
  'profile/updateProfileAsync',
  async (payload: { profileUpdates: Partial<ProfileData>; apiPayload: any }, { rejectWithValue }) => {
    try {
      const response = await updateUserProfileClient(payload.apiPayload);
      
      if (response.errorStatus) {
        return rejectWithValue(response.error);
      }
      
      return {
        profileUpdates: payload.profileUpdates,
        apiResponse: response.data
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setProfile(state, action: PayloadAction<ProfileData>) {
      state.data = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    updateProfile(state, action: PayloadAction<Partial<ProfileData>>) {
      if (state.data) {
        state.data = { ...state.data, ...action.payload };
      }
    },
    clearProfile(state) {
      state.data = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (state.data) {
          state.data = { ...state.data, ...action.payload.profileUpdates };
        }
      })
      .addCase(updateProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setProfile, updateProfile, clearProfile, clearError } = profileSlice.actions;
export default profileSlice.reducer;
